import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import {
  CuraFillField, CuraFitResult, CuraFormFill, CuraFormFillService,
} from '../../../core/services/cura-form-fill.service';
import { UserService } from '../../../core/services/user.service';
import { splitQuestion } from '../../../core/utils/form-question';
import { FLOATING_DIALOG_LAYOUT_KEYS } from '../../../core/services/floating-dialog-layout.service';

/**
 * Review a completed form: the form itself on the left, the real document on the right.
 *
 * tasks/cura-form-review-editor.md
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  THE PREVIEW IS THE ARTEFACT, NOT A PICTURE OF IT
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * Lanes A and B both hand back a real editable AcroForm PDF, and the viewer binds two-way to its
 * fields through `formData`. So an edit on the left sets a value in `formData` and the page
 * updates instantly — no re-render, no round trip, and no second implementation of the layout.
 *
 * That last part is the point. Re-rendering in a web worker would mean porting the fit pass
 * (measure, shrink, wrap, comb-split, truncate) into the browser, and two implementations of
 * "does this fit" drift. The failure mode is the worst available: looked right in the preview,
 * clipped in the file that was sent. `checkFit` asks the server's own renderer instead.
 */
@Component({
  selector: 'app-cura-form-review-dialog',
  templateUrl: './cura-form-review-dialog.component.html',
  styleUrls: ['./cura-form-review-dialog.component.scss'],
})
export class CuraFormReviewDialogComponent implements OnInit {
  fill?: CuraFormFill;
  loading = true;
  saving = false;
  error: string | null = null;

  pdfUrl: string | null = null;

  /**
   * Two-way bound to the viewer, keyed by the name each field carries inside the PDF.
   * Writing here IS the live preview.
   */
  formData: { [pdfFieldName: string]: any } = {};

  /** fieldId -> the operator's value. ONLY changed fields; see `accept`. */
  edits = new Map<string, string>();

  /** fieldId -> what the server says about whether it still fits. */
  fitResults = new Map<string, CuraFitResult>();
  /** Where the pane's size and place are remembered between reviews. */
  readonly layoutKey = FLOATING_DIALOG_LAYOUT_KEYS.curaFormReview;

  signIfPossible = false;
  canSign = false;
  promoting = false;
  promoted = false;

  selectedFieldId: string | null = null;
  /** Two-way with the viewer, so selecting a question on the left turns to its page. */
  viewerPage = 1;
  /** Off by default: the panel is the form, so it shows the form. */
  onlyNeedsYou = false;
  search = '';

  private fitQueue$ = new Subject<CuraFillField>();
  private destroy$ = new Subject<void>();
  private pendingFit = new Map<string, string>();
  private questions = new Map<string, { section: string | null; question: string }>();

  constructor(
    private service: CuraFormFillService,
    private userService: UserService,
    private dialogRef: MatDialogRef<CuraFormReviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { fillId: string },
  ) { }

  ngOnInit(): void {
    this.canSign = !!this.userService.canUserSign;

    // Debounced so a burst of typing costs one call, not one per keystroke.
    this.fitQueue$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.flushFitChecks());

    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private load(): void {
    this.loading = true;
    this.service.get(this.data.fillId).subscribe({
      next: (fill) => {
        this.fill = fill;
        this.promoted = !!fill.promoted;
        this.loading = false;
        this.seedFormData();
        this.refreshPdf();
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.error?.message || 'Could not load that form.';
      },
    });
  }

  /** Prime the viewer binding from what the fill produced. */
  private seedFormData(): void {
    const next: { [k: string]: any } = {};
    for (const f of this.fill?.fields || []) {
      if (!f.pdfFieldName) continue;
      next[f.pdfFieldName] = f.kind === 'checkbox'
        ? /^(yes|true|on|1)$/i.test(String(f.value ?? ''))
        : (f.pdfValue ?? f.value ?? '');
    }
    this.formData = next;
  }

  /**
   * Load the document into the right-hand pane.
   *
   * Uses the PREVIEW endpoint, not the download one. This used to read through `downloadUrl`,
   * which refuses anything unaccepted — so the pane sat empty saying "accept the form to see it
   * here" for the whole time the doctor was editing. Correcting a form you cannot see, and
   * having to accept it before you are allowed to look, is the wrong way round.
   */
  private refreshPdf(): void {
    if (!this.fill?.canPreview) { this.pdfUrl = null; return; }
    this.service.previewUrl(this.fill.id).subscribe({
      next: (d) => { this.pdfUrl = d.url; },
      error: () => { this.pdfUrl = null; },
    });
  }

  // ── the form on the left ──────────────────────────────────────────────────────────

  get allFields(): CuraFillField[] { return this.fill?.fields || []; }

  get visibleFields(): CuraFillField[] {
    let fields = this.allFields;
    if (this.onlyNeedsYou) {
      const flagged = new Set((this.fill?.needsYou || []).map(n => n.fieldId));
      fields = fields.filter(f => flagged.has(f.fieldId) || f.findings.length
        || this.fitResults.get(f.fieldId)?.fits === false);
    }
    const q = this.search.trim().toLowerCase();
    if (q) {
      fields = fields.filter(f => (f.label || '').toLowerCase().includes(q)
        || (f.itemRef || '').toLowerCase().includes(q)
        || String(this.valueOf(f) || '').toLowerCase().includes(q));
    }
    return fields;
  }

  /** Page boundaries, so the panel can show where one page ends and the next begins. */
  isFirstOnPage(field: CuraFillField, index: number): boolean {
    const list = this.visibleFields;
    return index === 0 || list[index - 1].page !== field.page;
  }

  /** The field's heading and its question, split so the heading is shown once per run of fields. */
  question(field: CuraFillField): { section: string | null; question: string } {
    let split = this.questions.get(field.fieldId);
    if (!split) {
      split = splitQuestion(field.label);
      this.questions.set(field.fieldId, split);
    }
    return split;
  }

  isFirstInSection(field: CuraFillField, index: number): boolean {
    const section = this.question(field).section;
    if (!section) return false;
    const list = this.visibleFields;
    return this.isFirstOnPage(field, index) || this.question(list[index - 1]).section !== section;
  }

  valueOf(field: CuraFillField): string {
    return this.edits.has(field.fieldId)
      ? this.edits.get(field.fieldId)!
      : (field.value ?? '');
  }

  isChecked(field: CuraFillField): boolean {
    return /^(yes|true|on|1)$/i.test(this.valueOf(field));
  }

  // ── editing ───────────────────────────────────────────────────────────────────────

  /**
   * Record an edit, push it to the viewer, and queue a fit check.
   *
   * An edit that returns the field to its original value is REMOVED rather than recorded as a
   * change — otherwise accepting would re-source a value the operator never actually altered.
   */
  setValue(field: CuraFillField, raw: any): void {
    const value = field.kind === 'checkbox'
      ? (raw ? 'Yes' : 'No')
      : String(raw ?? '');

    const original = field.value ?? '';
    if (value === original) this.edits.delete(field.fieldId);
    else this.edits.set(field.fieldId, value);

    // The live preview: writing here updates the PDF beside it, with no round trip.
    if (field.pdfFieldName) {
      this.formData = {
        ...this.formData,
        [field.pdfFieldName]: field.kind === 'checkbox' ? value === 'Yes' : value,
      };
    }

    if (this.service.needsFitCheck(field)) {
      this.pendingFit.set(field.fieldId, value);
      this.fitQueue$.next(field);
    } else {
      // A control that cannot overflow has nothing to ask about.
      this.fitResults.delete(field.fieldId);
    }
  }

  /**
   * The viewer edited a field directly — mirror it back into the panel.
   *
   * Signature fields are skipped (task R4.8). The panel refuses to edit one, but the viewer
   * has no such rule, and a value picked up here would be posted to `accept` as though a
   * person had typed it. A signature is applied by the signing step or by hand, never by us.
   */
  onViewerFormData(data: { [k: string]: any }): void {
    if (!data || !this.fill) return;
    for (const field of this.fill.fields) {
      if (field.kind === 'signature') continue;
      if (!field.pdfFieldName || !(field.pdfFieldName in data)) continue;
      const incoming = data[field.pdfFieldName];
      const asText = field.kind === 'checkbox' ? (incoming ? 'Yes' : 'No') : String(incoming ?? '');
      if (asText === this.valueOf(field)) continue;
      const original = field.value ?? '';
      if (asText === original) this.edits.delete(field.fieldId);
      else this.edits.set(field.fieldId, asText);
    }
    this.formData = { ...data };
  }

  private flushFitChecks(): void {
    if (!this.fill || !this.pendingFit.size) return;
    const values = [...this.pendingFit.entries()].map(([fieldId, value]) => ({ fieldId, value }));
    this.pendingFit.clear();
    this.service.checkFit(this.fill.id, values)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          const viewer = { ...this.formData };
          for (const r of results) {
            this.fitResults.set(r.fieldId, r);
            // The page carries the value as the renderer writes it, not as it was typed.
            const field = this.allFields.find((f) => f.fieldId === r.fieldId);
            if (field?.pdfFieldName && field.kind !== 'checkbox' && typeof r.wouldRender === 'string') {
              viewer[field.pdfFieldName] = r.wouldRender;
            }
          }
          this.formData = viewer;
        },
        error: () => { /* a missing fit hint must never block editing */ },
      });
  }

  fitFor(field: CuraFillField): CuraFitResult | undefined {
    return this.fitResults.get(field.fieldId);
  }

  // ── presentation ──────────────────────────────────────────────────────────────────

  /**
   * Select a question and turn the document to it (task R2.4).
   *
   * Deselecting deliberately does NOT turn back — the operator's place in the document is
   * theirs, and yanking the page away on a stray click is worse than leaving it.
   */
  selectField(fieldId: string): void {
    this.selectedFieldId = this.selectedFieldId === fieldId ? null : fieldId;
    if (!this.selectedFieldId) return;
    const field = this.allFields.find(f => f.fieldId === fieldId);
    if (field) this.goToPage(field.page);
  }

  /** Jump the document to a page — from a divider, or from selecting a question (task R1.7). */
  goToPage(page: number): void {
    const next = (page ?? 0) + 1;
    if (next > 0) this.viewerPage = next;
  }

  reasonLabel(reason: string): string { return this.service.reasonLabel(reason); }
  sourceLabel(field: CuraFillField): string { return this.service.sourceLabel(field.source); }

  confidenceClass(field: CuraFillField): string {
    if (field.confidence === null) return '';
    if (field.confidence >= 0.85) return 'conf-high';
    if (field.confidence >= 0.6) return 'conf-mid';
    return 'conf-low';
  }

  dispositionLabel(field: CuraFillField): string {
    if (this.edits.has(field.fieldId)) return 'You changed this';
    switch (field.disposition) {
      case 'filled': return 'Completed by CURA';
      case 'prefilled': return 'Already on the form';
      default: return 'Left blank';
    }
  }

  get editCount(): number { return this.edits.size; }
  get needsYou() { return this.fill?.needsYou || []; }
  get overflowCount(): number {
    return [...this.fitResults.values()].filter(r => !r.fits).length;
  }

  /** Restore a field to whatever the fill produced (open question §9.2 — kept, it makes editing safe). */
  revert(field: CuraFillField): void {
    this.edits.delete(field.fieldId);
    this.fitResults.delete(field.fieldId);
    if (field.pdfFieldName) {
      this.formData = {
        ...this.formData,
        [field.pdfFieldName]: field.kind === 'checkbox'
          ? /^(yes|true|on|1)$/i.test(String(field.value ?? ''))
          : (field.value ?? ''),
      };
    }
  }

  // ── finishing ─────────────────────────────────────────────────────────────────────

  accept(): void {
    if (!this.fill || this.saving) return;
    this.saving = true;
    this.error = null;
    // Only what changed — see the service note on provenance.
    const edits = [...this.edits.entries()].map(([fieldId, value]) => ({ fieldId, value }));
    this.service.accept(this.fill.id, edits, false).subscribe({
      next: (fill) => {
        this.fill = fill;
        this.edits.clear();
        this.fitResults.clear();
        this.saving = false;
        this.seedFormData();
        this.refreshPdf();
      },
      error: (err) => {
        this.saving = false;
        this.error = err?.error?.error?.message || 'Could not finalise that form.';
      },
    });
  }

  promoteToTimeline(): void {
    if (!this.fill || this.promoting || this.promoted) return;
    this.promoting = true;
    this.error = null;
    this.service.promote(this.fill.id, { title: `Completed form — ${this.fill.fileName}` })
      .subscribe({
        next: () => {
          this.promoting = false;
          this.promoted = true;
          if (this.fill) this.fill.promoted = true;
        },
        error: (err) => {
          this.promoting = false;
          this.error = err?.error?.error?.message || 'Could not add that form to the timeline.';
        },
      });
  }

  download(variant: 'editable' | 'signed'): void {
    if (!this.fill) return;
    this.service.downloadUrl(this.fill.id, variant).subscribe({
      // A new tab, not this one: the app guards leaving the page. The link carries a save-as
      // header, so the browser downloads it under its proper name and closes the tab.
      next: (d) => { window.open(d.url, '_blank'); },
      error: (err) => {
        this.error = err?.error?.error?.message || 'Could not prepare that download.';
      },
    });
  }

  close(): void { this.dialogRef.close(this.fill); }
}
